import React, { useEffect, useState } from "react";
import { Select, Skeleton } from "antd";
import { useDebounce } from "@uidotdev/usehooks";
import { useGetDropdownsQuery } from "../redux/api/dropdowns";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedDropdownValues } from "../redux/store/genericReportingSlice";

function DropdownContainer(props) {
  const [dropdownName, setDropdownName] = useState(props.dropdown_name);
  const [options, setOptions] = useState([]);
  const [error, setError] = useState("");
  const debouncedDropdownName = useDebounce(dropdownName, 100);
  const debouncedError = useDebounce(error, 3000);
  const dispatch = useDispatch();
  const selectedClient = useSelector((state) => state.genericReporting.selectedDropdownValues['client_name']);
  const selectedRegion = useSelector((state) => state.genericReporting.selectedDropdownValues['client_region']);
  const selectedValues = useSelector((state) => state.genericReporting.selectedDropdownValues[dropdownName] ?? ((dropdownName === 'single_select_client_region' || dropdownName === 'client_name') ? '' : []));
  const currentDropdownGroup = useSelector((state) => state.genericReporting.currentDropdownGroup);
  const { preserveUserSelections = true, includeTitle = true } = props;
  const isSharedDropdown = dropdownName === 'client_name' || dropdownName === 'client_region' || dropdownName === 'single_select_client_region';

  const { data: apiData, isLoading, isError, isFetching } = useGetDropdownsQuery({ 
    data: { 
      dropdown_name: debouncedDropdownName,
      ...(dropdownName !== 'client_name' && selectedClient ? { client_name: selectedClient } : {}),
      ...(dropdownName !== 'client_region' && dropdownName !== 'client_name' && selectedRegion ? { client_region: selectedRegion } : {}),
      ...(props.extraApiParams || {})
    },
    dropdown_group: currentDropdownGroup
  });

  useEffect(() => {
    if (apiData) {
      setOptions(apiData.options);
      
      // If there's only one option, automatically set it as the value
      if (apiData.default && apiData.options.length === 1) {
        const singleValue = apiData.multi_select 
          ? [apiData.options[0]]
          : apiData.options[0];
        // Only dispatch if the value is different
        if (JSON.stringify(selectedValues) !== JSON.stringify(singleValue)) {
          dispatch(setSelectedDropdownValues({ dropdownName, value: singleValue }));
          if (props.onChange) {
            props.onChange(singleValue);
          }
        }
      } else if (apiData.default && (!preserveUserSelections || !selectedValues.length)) {
        const defaultValues = apiData.multi_select 
          ? (Array.isArray(apiData.default) ? apiData.default : [apiData.default])
          : (Array.isArray(apiData.default) ? apiData.default[0] : apiData.default);
        // Only dispatch if the value is different
        if (JSON.stringify(selectedValues) !== JSON.stringify(defaultValues)) {
          dispatch(setSelectedDropdownValues({ dropdownName, value: defaultValues }));
        }
      }
      setError("");
    } else if (isError) {
      setOptions([]);
      if (!isSharedDropdown) {
        dispatch(setSelectedDropdownValues({ dropdownName, value: dropdownName === 'single_select_client_region' ? '' : [] }));
      }
      setError("Error loading dropdown options");
    } else if (isLoading || isFetching) {
      setOptions([]);
      if (!isSharedDropdown) {
        dispatch(setSelectedDropdownValues({ dropdownName, value: [] }));
      }
      setError("");
    } else {
      setOptions([]);
      if (!isSharedDropdown) {
        dispatch(setSelectedDropdownValues({ dropdownName, value: [] }));
      }
      setError("Error loading dropdown options");
    }
  }, [apiData, isError, isLoading, dropdownName, dispatch, props.onChange, isFetching]);

  useEffect(() => {
    if (isError) {
      setOptions([]);
      if (!isSharedDropdown) {
        dispatch(setSelectedDropdownValues({ dropdownName, value: [] }));
      }
      setError("Error loading dropdown options");
    }
  }, [isError, dropdownName, dispatch, isSharedDropdown]);

  useEffect(() => {
    setDropdownName(props.dropdown_name);
  }, [props.dropdown_name]);

  const handleChange = (value) => {
    dispatch(setSelectedDropdownValues({ dropdownName, value }));
    if (props.onChange) {
      props.onChange(value);
    }
  };

  // If there's only one option, don't render the dropdown
  if (!isFetching && !isError && options.length === 1) {
    return null;
  }

  return (
    <>
        {debouncedError && !isLoading && isError ? (
          <>
          </>
        ) : (
          <div style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            marginLeft: "0.5vw",
            marginRight: "0.5vw",
            minWidth: "10vw",
          }}>
            {!isFetching && !isError && options.length > 0 && (
              <>
                {includeTitle && (
                  <h4 style={{ minHeight: "1.2em" }}>
                    {apiData?.dropdown_title ? apiData?.dropdown_title : "Select an option"}
                  </h4>
                )}
                <Select
                  mode={apiData?.multi_select ? "multiple" : undefined}
                  style={{ width: apiData?.multi_select ? "12vw" : "10vw" }}
                  placeholder={"Select an option"}
                  value={selectedValues}
                  onChange={handleChange}
                  loading={isFetching}
                  maxTagCount="responsive"
                  maxTagTextLength={6}
                  popupMatchSelectWidth={false}
                  showSearch
                  options={options.map((option) =>
                    typeof option === 'object' ? option : {
                      value: option,
                      label: option.toString(),
                    }
                  )}
                />
              </>
            )}
            {isFetching && (
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                <h4 style={{ minHeight: "1.2em" }}>
                <Skeleton.Input active size="small" style={{ width: '4vw', height: '1.2em' }} />
                </h4>
                <Select
                  mode="multiple"
                  style={{ width: '10vw' }}
                  placeholder="Select an option"
                  loading={isFetching}
                  maxTagCount="responsive"
                  showSearch
                />
              </div>
            )}         
          </div>
        )}
    </>
  );
}

export default DropdownContainer;
